// adminMode — tryb widoku Admina MO / AK (MONIT 2026-10-11_0125, etap E5; W9, W10, W14, R7 meldunku 1305).
//
// Guzik MO / AK w nagłówku Admina przełącza CAŁY widok: albo terminy montażu, albo terminy Auskundung, nigdy
// oba naraz (W10). Telefon pamięta ostatni tryb w localStorage `fiberops_admin_mode`; filtry Admina siedzą
// w adresie (#), więc tryb jest osobno i linki trybu MO zostają bez zmian. W trybie AK adres listy niesie
// własne filtry ze znacznikiem `src=ak` (tydzień, dzień, team AK, projekt), bo te same klucze from/to/day
// jadą przez kreator w drodze AK (shared/akNav.js) i wracają na listę Admina.
// Czyste funkcje (node --test).

export const ADMIN_MODE_KEY = 'fiberops_admin_mode';
export const ADMIN_MODES = Object.freeze(['mo', 'ak']);
/** Klucz pozycji „Bez teamu AK” (termin AK bez linku `AK Teamy 👥`). */
export const AK_NO_TEAM = '__none__';
/** Dzień startowy Admina w trybie AK (rekomendacja HQ z monitu, DN1): cały tydzień; technik AK startuje od „dziś”. */
export const AK_ADMIN_DEFAULT_DAY = 'all';

/** Tryb z pamięci telefonu; brak, śmieci albo zablokowany localStorage = 'mo'. */
export function readAdminMode(storage) {
  try {
    return storage?.getItem?.(ADMIN_MODE_KEY) === 'ak' ? 'ak' : 'mo';
  } catch {
    return 'mo';
  }
}

/** Zapis trybu (prywatne okno / pełny dysk = cicho, tryb działa do przeładowania). */
export function writeAdminMode(storage, mode) {
  try { storage?.setItem?.(ADMIN_MODE_KEY, mode === 'ak' ? 'ak' : 'mo'); } catch { /* bez pamięci */ }
}

export function nextAdminMode(mode) {
  return mode === 'ak' ? 'mo' : 'ak';
}

/**
 * Tryb przy wejściu na listę Admina: link z `src=ak` (powrót z adresu otwartego z karty AK) = AK,
 * inaczej tryb zapamiętany na telefonie.
 */
export function initialAdminMode(query, storage) {
  if (query?.src === 'ak') return 'ak';
  return readAdminMode(storage);
}

/**
 * Filtry trybu AK z adresu listy. Bez `src=ak` → nic (tydzień = bieżący, dzień = domyślny Admina),
 * żeby parametry trybu MO (from/to zbioru KW) nie udawały tygodnia AK.
 * @returns {{ from: string|null, to: string|null, day: string, team: string, project: string }}
 */
export function parseAkAdminQuery(query) {
  if (query?.src !== 'ak') return { from: null, to: null, day: AK_ADMIN_DEFAULT_DAY, team: '', project: '' };
  const okDate = (s) => typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s);
  const from = okDate(query.from) && okDate(query.to) ? query.from : null;
  return {
    from,
    to: from ? query.to : null,
    day: query.day === 'all' || okDate(query.day) ? query.day : AK_ADMIN_DEFAULT_DAY,
    team: typeof query.akteam === 'string' ? query.akteam : '',
    project: typeof query.project === 'string' ? query.project : '',
  };
}

/**
 * Parametry adresu listy w trybie AK (#/admin/tasks?src=ak&from&to&day&akteam&project). Puste pomijane.
 * @param {{ week: {from:string,to:string}|null, day: string, team: string, project: string }} s
 * @returns {Record<string,string>|null} null = tydzień jeszcze nieznany (lista tygodni się ładuje)
 */
export function buildAkAdminParams({ week, day, team, project }) {
  if (!week?.from || !week?.to) return null;
  const p = { src: 'ak', from: week.from, to: week.to, day: day || AK_ADMIN_DEFAULT_DAY };
  if (team) p.akteam = team;
  if (project) p.project = project;
  return p;
}

/**
 * Zapytanie do adresu otwieranego z karty AK Admina (E5 pkt 4): droga AK (`src=ak`) + znacznik Admina
 * (`adm=1`: powrót na /admin/tasks zamiast listy technika) + filtry listy.
 */
export function akAdminCardQuery(state) {
  const p = buildAkAdminParams(state) || { src: 'ak' };
  return { ...p, adm: '1' };
}

/**
 * Lista teamów AK tygodnia (W9, R7 pkt 2): z terminów, NIE z `Aktualny Projekt` (oba teamy AK mają tam stare
 * projekty). Pozycja = { value: ID rekordu teamu, label, count }; terminy bez teamu = jedna pozycja
 * AK_NO_TEAM na końcu (label z i18n podaje ekran). Sort po etykiecie (pl).
 * @param {Array<{akTeams?: Array<{id:string,label:string}>}>} tasks
 */
export function akTeamOptions(tasks) {
  const byId = new Map();
  let none = 0;
  for (const t of tasks || []) {
    const teams = Array.isArray(t?.akTeams) ? t.akTeams : [];
    if (!teams.length) { none += 1; continue; }
    for (const team of teams) {
      if (!team?.id) continue;
      const cur = byId.get(team.id) || { value: team.id, label: team.label || team.id, count: 0 };
      cur.count += 1;
      byId.set(team.id, cur);
    }
  }
  const out = [...byId.values()].sort((a, b) => a.label.localeCompare(b.label, 'pl'));
  if (none) out.push({ value: AK_NO_TEAM, label: null, count: none });
  return out;
}

/**
 * Filtr listy AK Admina: team AK (ID rekordu albo AK_NO_TEAM) i projekt po ID rekordu (U6: nigdy po Prefixie).
 * Pusty filtr = wszystko.
 */
export function filterAkAdminTasks(tasks, { team = '', project = '' } = {}) {
  return (tasks || []).filter((t) => {
    if (project && t?.projektId !== project) return false;
    if (!team) return true;
    const teams = Array.isArray(t?.akTeams) ? t.akTeams : [];
    if (team === AK_NO_TEAM) return teams.length === 0;
    return teams.some((x) => x?.id === team);
  });
}

/** Etykieta teamów terminu na karcie Admina („AK Bernard”, „A · B”); null = bez teamu. */
export function akTeamLabel(task) {
  const teams = Array.isArray(task?.akTeams) ? task.akTeams : [];
  if (!teams.length) return null;
  return teams.map((x) => x.label || x.id).join(' · ');
}
